import React, { useState } from 'react';

const ModerationFilters = ({ filters, onFilterChange, onRefresh, activeStatus }) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const handleInputChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const handleReset = () => {
    onFilterChange({
      moderation_status: activeStatus,
      search: '',
      category_id: '',
      min_price: '',
      max_price: '',
      date_from: '',
      date_to: '',
      order_by: 'created_at',
      order_direction: 'ASC'
    });
  };

  const getStatusDescription = () => {
    const descriptions = {
      pending: 'Listings waiting for initial review and approval',
      flagged: 'Listings that have been flagged for attention',
      rejected: 'Listings that were previously rejected',
      approved: 'Listings that have been approved',
      all: 'All listings across all moderation statuses'
    };
    return descriptions[activeStatus] || descriptions.all;
  };

  return (
    <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            Filter <span className="text-[#ff660e] capitalize">{activeStatus}</span> Listings
          </h3>
          <p className="text-gray-400 text-sm">{getStatusDescription()}</p>
        </div>
        <button
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          className="px-4 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02] bg-gray-750 border border-gray-600 hover:border-[#ff660e] text-gray-300 hover:text-white"
        >
          <span>{isAdvancedOpen ? '▲' : '▼'}</span>
          <span>{isAdvancedOpen ? 'Hide Advanced' : 'Show Advanced'}</span>
        </button>
      </div>

      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <span className="text-[#ff660e]">🔍</span>
            <span>Search</span>
          </label>
          <input
            type="text"
            placeholder="Search titles, descriptions, stores..."
            value={filters.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
            className="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
          />
        </div>

        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <span className="text-[#ff660e]">📅</span>
            <span>From Date</span>
          </label>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => handleInputChange('date_from', e.target.value)}
            className="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <span className="text-[#ff660e]">📅</span>
            <span>To Date</span>
          </label>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => handleInputChange('date_to', e.target.value)}
            className="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Advanced Filters */}
      {isAdvancedOpen && (
        <div className="animate-fadeIn">
          <div className="border-t border-gray-700 pt-6 mb-6">
            <h4 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <span className="text-[#ff660e]">⚙️</span>
              <span>Advanced Filters</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Price Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Min Price (₦)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={filters.min_price}
                  onChange={(e) => handleInputChange('min_price', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Max Price (₦)</label>
                <input
                  type="number"
                  placeholder="1000000"
                  value={filters.max_price}
                  onChange={(e) => handleInputChange('max_price', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
                />
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sort By</label>
                <select
                  value={filters.order_by}
                  onChange={(e) => handleInputChange('order_by', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all appearance-none"
                >
                  <option value="created_at">Date Created</option>
                  <option value="price">Price</option>
                  <option value="title">Title</option>
                  <option value="view_count">Views</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sort Direction</label>
                <select
                  value={filters.order_direction}
                  onChange={(e) => handleInputChange('order_direction', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all appearance-none"
                >
                  <option value="ASC">Ascending (Oldest First)</option>
                  <option value="DESC">Descending (Newest First)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-700">
        <div className="text-sm text-gray-400">
          <p>Quickly filter listings to focus on specific moderation needs.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className="px-5 py-3 rounded-lg font-medium border-2 border-gray-600 text-gray-300 hover:text-white hover:border-[#ff660e] hover:bg-gray-700 transition-all duration-200 flex items-center gap-2"
          >
            <span className="text-gray-400 hover:text-[#ff660e] transition-colors">🔄</span>
            <span>Reset Filters</span>
          </button>
          
          <button
            onClick={onRefresh}
            className="px-5 py-3 rounded-lg font-semibold bg-gradient-to-r from-[#ff660e] to-orange-500 text-white hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] shadow-lg flex items-center gap-2"
          >
            <span>🔍</span>
            <span>Apply Filters</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModerationFilters;